import React, { useState } from 'react';
import { ArrowRight, CheckCircle, ChevronDown, ChevronUp } from 'lucide-react';
import ConditionExitPopup from '../components/ConditionExitPopup';
import TrustStrip from '../components/TrustStrip';
import ClinicianCard from '../components/ClinicianCard';
import ServicesDropdown from '../components/ServicesDropdown';

const MenopauseLanding = () => {
  const [openFaq, setOpenFaq] = useState(null);

  const scrollToBooking = () => {
    // Tag the booking origin so the pixel knows this conversion came from /menopause.
    // Read again on /book after the GHL calendar fires its booked event.
    try { sessionStorage.setItem('glow_book_origin', 'menopause'); } catch { /* noop */ }
    window.location.href = '/book';
  };

  const painPoints = [
    {
      title: 'Hot flashes that hijack your day',
      pain: 'Are you peeling off layers in meetings, soaked through your sheets at 3am, or planning your outfits around when the next wave will hit?',
      description: 'Hot flashes and night sweats are not something to "just push through." They are your body screaming for hormonal support, and they can be calmed with the right plan.'
    },
    {
      title: 'Sleep that never feels like rest',
      pain: 'Are you exhausted but wide awake at 2am, watching the clock until your alarm, then dragging yourself through another day?',
      description: 'Estrogen and progesterone changes wreck your sleep architecture. No amount of melatonin or sleep hygiene can fix what is fundamentally a hormonal problem.'
    },
    {
      title: 'A body you do not recognize',
      pain: 'Are you watching weight settle around your middle no matter how clean you eat, while your favorite jeans sit unworn in the closet?',
      description: 'Menopausal weight gain is metabolic, not behavioral. Your body is storing fat differently because your hormones have shifted. You are not failing.'
    },
    {
      title: 'Intimacy that hurts instead of connects',
      pain: 'Are you avoiding sex with your partner because of dryness or pain, then feeling guilty, distant, and like a stranger in your own marriage?',
      description: 'Vaginal dryness, thinning tissue, and lost libido are physical symptoms of estrogen loss. They are treatable, and you should not have to choose between intimacy and comfort.'
    },
    {
      title: 'Brain fog stealing your sharpness',
      pain: 'Are you walking into rooms and forgetting why, losing words mid-sentence, or quietly worried this might be the start of something worse?',
      description: 'Menopausal brain fog is hormonal, not cognitive decline. When estrogen drops, so does your brain\'s ability to process and recall. Restoring balance restores clarity.'
    },
    {
      title: 'Feeling invisible in your own life',
      pain: 'Are you grieving the woman you used to be, wondering when you became someone who cancels plans, snaps at loved ones, and barely recognizes herself?',
      description: 'You did not lose yourself. Your hormones changed, and no one prepared you for what that does to your identity, energy, and joy. We help you find her again.'
    }
  ];

  const emotionalImpact = [
    {
      area: 'Your marriage',
      impact: 'Avoiding intimacy, snapping at your partner, feeling distant from someone you love deeply'
    },
    {
      area: 'Your career',
      impact: 'Doubting yourself in meetings, missing details you used to catch, wondering if you can keep up anymore'
    },
    {
      area: 'Your body',
      impact: 'A reflection that feels like a stranger, clothes that no longer fit, joints that ache for no reason'
    },
    {
      area: 'Your spirit',
      impact: 'Mourning the energy, confidence, and clarity you had at 35, while everyone tells you "this is just aging"'
    }
  ];

  const steps = [
    {
      number: '1',
      title: 'Tell us what you are losing',
      description: 'Share the symptoms, the silences, the things you have not told anyone. We listen with empathy, never dismissal.'
    },
    {
      number: '2',
      title: 'Get a full hormone evaluation',
      description: 'Comprehensive testing of estrogen, progesterone, testosterone, thyroid, and metabolic markers. Real data, not assumptions.'
    },
    {
      number: '3',
      title: 'Receive a personalized HRT plan',
      description: 'Bioidentical hormone therapy and targeted support designed around your symptoms, your history, and your goals.'
    },
    {
      number: '4',
      title: 'Feel like yourself again',
      description: 'Ongoing care, dose adjustments, and a clinician who tracks your progress so you keep getting better, not worse.'
    }
  ];

  const proofPoints = [
    {
      stat: '85%',
      title: 'Of women have symptoms',
      description: 'Nearly every woman experiences menopausal symptoms, but most are told to "tough it out" instead of getting real help.'
    },
    {
      stat: '7+ yrs',
      title: 'Average symptom duration',
      description: 'Without treatment, hot flashes, sleep loss, and mood symptoms can last 7 to 10 years. You do not have to suffer that long.'
    },
    {
      stat: '2-4 wks',
      title: 'To feel meaningful relief',
      description: 'With the right hormone plan, most women report better sleep, fewer hot flashes, and clearer thinking within weeks.'
    }
  ];

  const faqs = [
    {
      question: 'Is hormone replacement therapy (HRT) safe?',
      answer: 'Modern bioidentical HRT, when properly prescribed and monitored, is considered safe for most healthy women within 10 years of menopause onset. The outdated fears from the early 2000s study have been largely revised. We use the lowest effective dose and track your labs carefully.'
    },
    {
      question: 'I am already in menopause. Is it too late to start?',
      answer: 'It is not too late. Many women find significant relief from HRT even years into menopause, especially for symptoms like vaginal dryness, sleep, mood, bone health, and quality of life. Your timing matters less than your symptoms and goals.'
    },
    {
      question: 'Will HRT help me lose weight?',
      answer: 'Restoring hormonal balance can absolutely make weight management easier. We pair HRT with metabolic support, and for women who qualify, we offer GLP-1 medications to address stubborn menopausal weight gain.'
    },
    {
      question: 'Will I have to take hormones forever?',
      answer: 'Treatment plans are highly individualized. Some women use HRT for several years to navigate the transition, others use it longer for ongoing benefits like bone, brain, and cardiovascular protection. We review and adjust your plan regularly.'
    },
    {
      question: 'My doctor said my labs are "normal" but I feel terrible. Why?',
      answer: 'Standard reference ranges are population averages, not personal optimums. "Normal" labs do not mean you feel normal. We look at your symptoms alongside detailed hormone panels to find what is actually happening in your body, not just what fits in a box.'
    }
  ];

  return (
    <div className="pcos-page">
      {/* Header */}
      <header className="pcos-header">
        <div className="pcos-container pcos-header-inner">
          <a href="/" className="pcos-brand" data-testid="meno-brand-link">
            <img
              src="https://customer-assets.emergentagent.com/job_hormone-wellness-1/artifacts/emqp5y2s_symbol%20%281%29.svg"
              alt="Glow Health Logo"
              className="brand-symbol"
            />
            <span>Glow Health</span>
          </a>
          <div className="pcos-header-actions">
            <ServicesDropdown testIdPrefix="meno-nav-services" />
            <button className="pcos-btn pcos-btn-primary" onClick={scrollToBooking} data-testid="meno-header-cta">
              Book Your Free Consultation
            </button>
          </div>
        </div>
      </header>

      <main>
        {/* Hero */}
        <section className="pcos-hero">
          <div className="pcos-container pcos-hero-split">
            <div className="pcos-hero-content">
              <span className="pcos-eyebrow">Menopause Care</span>
              <h1 className="pcos-hero-headline">
                You are not "just getting older."<br />
                <span className="pcos-highlight">You are in menopause, and you deserve real care.</span>
              </h1>
              <p className="pcos-hero-sub">
                If you are exhausted from being told this is normal, from doctors who hand you an antidepressant instead of a hormone panel, from feeling like a ghost of who you used to be... you are not imagining this. Your hormones have changed. And so can how you feel.
              </p>
              <div className="pcos-hero-actions">
                <button className="pcos-btn pcos-btn-primary pcos-btn-large" onClick={scrollToBooking} data-testid="meno-hero-cta">
                  Get Real Relief
                  <ArrowRight size={18} />
                </button>
                <a href="#pain" className="pcos-btn pcos-btn-secondary" data-testid="meno-hero-secondary-cta">See If This Sounds Like You</a>
              </div>
              <p className="pcos-hero-note">Free consultation. No judgment. Finally, a clinician who listens.</p>
              <div className="meno-hero-credentials" data-testid="meno-hero-credentials">
                <p><strong>Utah-licensed clinician</strong> experienced in menopause care.</p>
                <p>Serving women in Utah via secure HIPAA video visits.</p>
              </div>
            </div>
            <div className="pcos-hero-image">
              <img
                src="https://images.unsplash.com/photo-1595433502559-d8f05e6a1041?w=640&h=760&fit=crop&auto=format"
                alt="A woman who finally got the care she needed"
                loading="lazy"
              />
              <div className="pcos-hero-image-badge">
                "I feel like myself again."
              </div>
            </div>
          </div>
        </section>

        <TrustStrip showLocation />

        {/* Proof line - non-condition-specific, policy-safe outcome statement */}
        <section className="meno-proofline" data-testid="meno-proofline">
          <div className="pcos-container">
            <p>
              Women describe finally feeling heard, with clearer options and a treatment plan that makes sense for them.
            </p>
          </div>
        </section>

        {/* Three-panel journey strip */}
        <section className="meno-journey" data-testid="meno-journey">
          <div className="pcos-container">
            <div className="meno-journey-grid">
              <figure className="meno-journey-panel" data-testid="meno-journey-panel-1">
                <div className="meno-journey-image">
                  <img src="/meno-panel-1.png" alt="A woman sitting quietly by a window, tired but dignified" loading="lazy" />
                </div>
                <figcaption>You are not imagining it.</figcaption>
              </figure>
              <figure className="meno-journey-panel" data-testid="meno-journey-panel-2">
                <div className="meno-journey-image">
                  <img src="/meno-panel-2.png" alt="A woman on a warm telehealth video visit with a clinician" loading="lazy" />
                </div>
                <figcaption>Talk with a clinician who understands menopause.</figcaption>
              </figure>
              <figure className="meno-journey-panel" data-testid="meno-journey-panel-3">
                <div className="meno-journey-image">
                  <img src="/meno-panel-3.png" alt="A woman walking peacefully in soft morning light" loading="lazy" />
                </div>
                <figcaption>A plan tailored to your body and your life.</figcaption>
              </figure>
            </div>
          </div>
        </section>

        {/* Specialist value prop */}
        <section className="meno-specialist" data-testid="meno-specialist">
          <div className="pcos-container">
            <p>
              <strong>Not a general telehealth clinic.</strong> We focus on perimenopause and menopause, so your symptoms are not minimized as "just stress."
            </p>
          </div>
        </section>

        {/* Pain Points */}
        <section className="pcos-section pcos-pain-section" id="pain">
          <div className="pcos-container">
            <div className="pcos-section-head">
              <span className="pcos-eyebrow">Why It Feels This Way</span>
              <h2>Why menopause can feel so overwhelming</h2>
              <p>Menopause is not just "the end of periods." It is a full-body transformation that touches your sleep, your sex life, your sanity, and your sense of self.</p>
            </div>
            <div className="pcos-pain-grid">
              {painPoints.map((point, index) => (
                <div key={index} className="pcos-pain-card" data-testid={`meno-pain-card-${index}`}>
                  <h3>{point.title}</h3>
                  <p className="pcos-pain-question">{point.pain}</p>
                  <p className="pcos-pain-explain">{point.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Emotional Impact */}
        <section className="pcos-section pcos-impact-section">
          <div className="pcos-container">
            <div className="pcos-impact-wrap">
              <div className="pcos-impact-content">
                <span className="pcos-eyebrow">The Hidden Cost</span>
                <h2>Menopause is not just a phase. It is changing every part of your life.</h2>
                <p className="pcos-impact-intro">
                  The symptoms are real. But the quiet losses underneath them are what break your heart.
                </p>
              </div>
              <div className="pcos-impact-grid">
                {emotionalImpact.map((item, index) => (
                  <div key={index} className="pcos-impact-card" data-testid={`meno-impact-card-${index}`}>
                    <h4>{item.area}</h4>
                    <p>{item.impact}</p>
                  </div>
                ))}
              </div>
              <p className="pcos-impact-cta">
                <strong>You are not done. You are not invisible. You just need the right care.</strong>
              </p>
            </div>
          </div>
        </section>

        {/* Quote */}
        <section className="pcos-section pcos-quote-section" style={{backgroundImage: "url('/quote-bg-menopause.png')"}}>
          <div className="pcos-container">
            <div className="pcos-quote-wrap">
              <blockquote>
                <p>"For three years my doctor told me to do yoga and drink more water. Six weeks on the right hormones and I finally slept through the night. I am so angry I waited."</p>
                <cite>Real patient story</cite>
              </blockquote>
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="pcos-section" id="plan">
          <div className="pcos-container">
            <div className="pcos-section-head">
              <span className="pcos-eyebrow">How We Help</span>
              <h2>How personalized telehealth can help</h2>
              <p>No more "this is just life now." No more being handed antidepressants for a hormonal problem. No more figuring it out alone.</p>
            </div>
            <div className="pcos-steps-grid">
              {steps.map((step, index) => (
                <div key={index} className="pcos-step-card" data-testid={`meno-step-card-${index}`}>
                  <div className="pcos-step-number">{step.number}</div>
                  <h3>{step.title}</h3>
                  <p>{step.description}</p>
                </div>
              ))}
            </div>
            <div className="pcos-steps-cta">
              <button className="pcos-btn pcos-btn-primary pcos-btn-large" onClick={scrollToBooking} data-testid="meno-steps-cta">
                Book Your Free Consultation
                <ArrowRight size={18} />
              </button>
              <p className="pcos-cta-note">No commitment. No pressure. Just answers.</p>
            </div>
          </div>
        </section>

        {/* Proof Points */}
        <section className="pcos-section pcos-proof-section">
          <div className="pcos-container">
            <div className="pcos-section-head">
              <span className="pcos-eyebrow">The Reality</span>
              <h2>You are not alone, even though it feels that way</h2>
            </div>
            <div className="pcos-proof-grid">
              {proofPoints.map((point, index) => (
                <div key={index} className="pcos-proof-card" data-testid={`meno-proof-card-${index}`}>
                  <strong>{point.stat}</strong>
                  <h3>{point.title}</h3>
                  <p>{point.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <ClinicianCard />

        {/* What we cover in your visit */}
        <section className="pcos-section meno-visit-section" data-testid="meno-visit-section">
          <div className="pcos-container">
            <div className="pcos-section-head">
              <span className="pcos-eyebrow">Your First Visit</span>
              <h2>What we cover in your visit</h2>
              <p>A relaxed, no-pressure 30-minute video visit. We listen first, then map a path forward.</p>
            </div>
            <ul className="meno-visit-list">
              <li>
                <CheckCircle size={18} />
                <span>A real conversation about your symptoms, sleep, mood, and what feels off.</span>
              </li>
              <li>
                <CheckCircle size={18} />
                <span>A review of any previous labs, medications, and your full health history.</span>
              </li>
              <li>
                <CheckCircle size={18} />
                <span>Clear, plain-language education about what is actually happening in your body.</span>
              </li>
              <li>
                <CheckCircle size={18} />
                <span>A personalized plan: targeted lab work, hormone therapy options, and lifestyle support if it fits.</span>
              </li>
              <li>
                <CheckCircle size={18} />
                <span>Honest answers about what to expect, costs, and timeline. No surprises.</span>
              </li>
            </ul>
          </div>
        </section>

        {/* Who this is right for */}
        <section className="pcos-section meno-fit-section" data-testid="meno-fit-section">
          <div className="pcos-container">
            <div className="pcos-section-head">
              <span className="pcos-eyebrow">A Quick Honest Check</span>
              <h2>Who My Glow Health is right for</h2>
              <p>We are not for everyone, and that is the point. Here is who tends to feel the biggest shift working with us.</p>
            </div>
            <div className="meno-fit-grid">
              <div className="meno-fit-card meno-fit-yes" data-testid="meno-fit-yes">
                <h3>This is right for you if</h3>
                <ul>
                  <li>You live in Utah and want secure telehealth visits.</li>
                  <li>You suspect perimenopause or menopause is driving how you feel.</li>
                  <li>You want a clinician who specializes in this, not a generalist.</li>
                  <li>You are open to evidence-based hormone therapy if it is right for you.</li>
                  <li>You want a plan tailored to your body, not a template.</li>
                </ul>
              </div>
              <div className="meno-fit-card meno-fit-no" data-testid="meno-fit-no">
                <h3>This is probably not for you if</h3>
                <ul>
                  <li>You are looking for an in-person clinic visit.</li>
                  <li>You want a quick prescription with no lab work or follow-up.</li>
                  <li>You are not interested in hormone therapy as one possible option.</li>
                  <li>You prefer a one-size-fits-all wellness program.</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="pcos-section pcos-cta-section" id="book">
          <div className="pcos-container">
            <div className="pcos-cta-wrap">
              <div className="pcos-cta-content">
                <span className="pcos-eyebrow">Take The First Step</span>
                <h2>Stop suffering through menopause alone</h2>
                <ul className="pcos-check-list">
                  <li>
                    <CheckCircle size={18} />
                    <span>Finally understand exactly what your hormones are doing</span>
                  </li>
                  <li>
                    <CheckCircle size={18} />
                    <span>Get a personalized HRT plan, not a one-size-fits-all script</span>
                  </li>
                  <li>
                    <CheckCircle size={18} />
                    <span>Work with clinicians who specialize in menopause, not generalists who shrug</span>
                  </li>
                  <li>
                    <CheckCircle size={18} />
                    <span>Get your sleep, your body, your intimacy, and your spark back</span>
                  </li>
                </ul>
                <button className="pcos-btn pcos-btn-primary pcos-btn-large" onClick={scrollToBooking} data-testid="meno-mid-cta">
                  Book Your Free Consultation
                  <ArrowRight size={18} />
                </button>
                <p className="pcos-cta-note">Free & confidential. Limited spots available each week.</p>
              </div>
              <div className="pcos-cta-visual">
                <div className="pcos-guarantee-card">
                  <h3>Our Promise</h3>
                  <p>If you do not feel meaningfully better within 60 days of starting your plan, we will keep working with you for free until you do. No questions asked.</p>
                </div>
                <div className="pcos-testimonial-card">
                  <p>"I cried in my first appointment because she actually listened. Three months later I feel more like myself than I have in five years."</p>
                  <span>Menopause patient, age 54</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="pcos-section">
          <div className="pcos-container">
            <div className="pcos-section-head">
              <span className="pcos-eyebrow">Questions</span>
              <h2>What you are probably wondering</h2>
            </div>
            <div className="pcos-faq-list">
              {faqs.map((faq, index) => (
                <div
                  key={index}
                  className={`pcos-faq-item ${openFaq === index ? 'open' : ''}`}
                  data-testid={`meno-faq-item-${index}`}
                >
                  <button
                    className="pcos-faq-question"
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                    data-testid={`meno-faq-toggle-${index}`}
                  >
                    <span>{faq.question}</span>
                    {openFaq === index ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                  </button>
                  {openFaq === index && (
                    <div className="pcos-faq-answer">
                      <p>{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="pcos-section pcos-final-cta">
          <div className="pcos-container">
            <div className="pcos-final-cta-content">
              <h2>You have suffered long enough</h2>
              <p>
                Every night of broken sleep, every hot flash in a meeting, every moment of feeling invisible in your own body, is another day stolen from a life you have worked too hard to build. You do not have to settle for this.
              </p>
              <button className="pcos-btn pcos-btn-primary pcos-btn-large" onClick={scrollToBooking} data-testid="meno-final-cta">
                Book Your Free Consultation Now
                <ArrowRight size={18} />
              </button>
              <p className="pcos-final-note">15 minutes could change the next 15 years.</p>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="pcos-footer">
        <div className="pcos-container pcos-footer-inner">
          <span>© {new Date().getFullYear()} Glow Health LLC</span>
          <div className="pcos-footer-links">
            <a href="/privacy-policy">Privacy Policy</a>
            <span>·</span>
            <a href="/terms-of-service">Terms of Service</a>
          </div>
        </div>
      </footer>
      <ConditionExitPopup condition="menopause" />
    </div>
  );
};

export default MenopauseLanding;
